/**
 * ErrorBanner.tsx
 * =============================================================================
 * Professional error notification banner component.
 * Uses warm amber tones instead of aggressive red.
 * Fixed overlay positioning - doesn't push content down.
 * 
 * @author Neuro-Audio Lab
 * @version 3.0.0 - Rebranding
 */

import { X, AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface ErrorBannerProps {
  message: string;
  onDismiss?: () => void;
  autoDismiss?: boolean;
  autoDismissDelay?: number; // milliseconds
}

// =============================================================================
// ERROR BANNER COMPONENT
// =============================================================================

export function ErrorBanner({
  message,
  onDismiss,
  autoDismiss = true,
  autoDismissDelay = 5000,
}: ErrorBannerProps) {
  const [isVisible, setIsVisible] = useState(true);

  // Auto-dismiss logic
  useEffect(() => {
    if (autoDismiss && autoDismissDelay > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, autoDismissDelay);

      return () => clearTimeout(timer);
    }
  }, [autoDismiss, autoDismissDelay]);

  // Handle dismiss with animation
  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      onDismiss?.();
    }, 300); // Match animation duration
  };

  if (!isVisible) return null;

  return (
    <div
      className={`
        error-banner
        flex items-center gap-3
        ${isVisible ? 'animate-slide-down' : 'animate-slide-up'}
      `}
      role="alert"
      aria-live="assertive"
    >
      {/* Warning Icon */}
      <div className="flex-shrink-0">
        <AlertTriangle className="w-5 h-5 text-amber-300" />
      </div>

      {/* Message */}
      <p className="flex-1 text-sm font-medium text-white">
        {message}
      </p>

      {/* Dismiss Button */}
      {onDismiss && (
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 rounded-lg hover:bg-white/10 transition-colors duration-200"
          aria-label="Dismiss error"
        >
          <X className="w-4 h-4 text-white/70 hover:text-white" />
        </button>
      )}

      {/* Progress bar for auto-dismiss */}
      {autoDismiss && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10 rounded-b-xl overflow-hidden">
          <div
            className="h-full bg-amber-400"
            style={{
              animation: `shrink ${autoDismissDelay}ms linear forwards`,
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }

        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }

        @keyframes slide-up {
          from {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
          to {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
        }

        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }

        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

// =============================================================================
// ERROR MANAGER HOOK (Optional)
// =============================================================================

/**
 * Hook para gestionar múltiples errores en la app
 * Uso:
 * 
 * const { showError, clearError } = useErrorManager();
 * 
 * // Mostrar error
 * showError("No se pudo cargar el audio");
 * 
 * // Limpiar manualmente
 * clearError();
 */

export function useErrorManager() {
  const [error, setError] = useState<string | null>(null);

  const showError = (message: string) => {
    setError(message);
  };

  const clearError = () => {
    setError(null);
  };

  return {
    error,
    showError,
    clearError,
  };
}

// =============================================================================
// ERROR MANAGER PROVIDER (Optional - para contexto global)
// =============================================================================

/**
 * Componente que renderiza el ErrorBanner globalmente
 * 
 * Uso en App.tsx o layout principal:
 * 
 * function App() {
 *   const { error, clearError } = useErrorManager();
 *   
 *   return (
 *     <>
 *       {error && (
 *         <ErrorBanner 
 *           message={error} 
 *           onDismiss={clearError} 
 *         />
 *       )}
 *       <YourContent />
 *     </>
 *   );
 * }
 */

export default ErrorBanner;